import React from "react";
import { AlertTriangle, CheckCircle2, Calendar, Users, ArrowRight, Clock } from "lucide-react";
import { AccountDeletionBlocker } from "../api/user/useAccountDeletionValidation";
import { formatDateDisplay } from "../utils/dateUtils";

interface AccountDeletionValidationProps {
	canDelete: boolean;
	blockers: AccountDeletionBlocker[];
	message: string;
	loading?: boolean;
}

export function AccountDeletionValidation({
	canDelete,
	blockers,
	message,
	loading = false
}: AccountDeletionValidationProps) {
	const getBlockerIcon = (type: AccountDeletionBlocker['type']) => {
		switch (type) {
			case 'booked_events':
				return <Calendar className="h-5 w-5 text-orange-600" />;
			case 'applied_events':
				return <Clock className="h-5 w-5 text-blue-600" />;
			case 'group_memberships':
				return <Users className="h-5 w-5 text-purple-600" />;
			default:
				return <AlertTriangle className="h-5 w-5 text-red-600" />;
		}
	};

	const getBlockerColor = (type: AccountDeletionBlocker['type']) => {
		switch (type) {
			case 'booked_events':
				return 'border-orange-200 bg-orange-50';
			case 'applied_events':
				return 'border-blue-200 bg-blue-50';
			case 'group_memberships':
				return 'border-purple-200 bg-purple-50';
			default:
				return 'border-red-200 bg-red-50';
		}
	};

	const getBlockerTextColor = (type: AccountDeletionBlocker['type']) => {
		switch (type) {
			case 'booked_events':
				return 'text-orange-700';
			case 'applied_events':
				return 'text-blue-700';
			case 'group_memberships':
				return 'text-purple-700';
			default:
				return 'text-red-700';
		}
	};

	const formatEventDetails = (details: any[]) => {
		return details.slice(0, 3).map((event, index) => (
			<div key={index} className="text-sm">
				<span className="font-medium">{event.event_type || `Event ${event.event_id}`}</span>
				{event.event_date && (
					<span className="text-gray-600 ml-2">
						am {formatDateDisplay(event.event_date)}
					</span>
				)}
				{event.start_time && (
					<span className="text-gray-600 ml-2">
						um {event.start_time}
					</span>
				)}
			</div>
		));
	};

	const formatGroupDetails = (details: any[]) => {
		return details.slice(0, 3).map((group, index) => (
			<div key={index} className="text-sm">
				<span className="font-medium">{group.group_name}</span>
				<span className="text-gray-600 ml-2">
					({group.role === 'creator' ? 'Ersteller' : 'Mitglied'})
				</span>
				{group.joined_at && (
					<span className="text-gray-600 ml-2">
						- {new Date(group.joined_at).toLocaleDateString('de-DE')}
					</span>
				)}
			</div>
		));
	};

	if (loading) {
		return (
			<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
				<div className="flex items-center gap-3">
					<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
					<p className="text-gray-600">Überprüfe Account-Berechtigung...</p>
				</div>
			</div>
		);
	}

	if (canDelete) {
		return (
			<div className="bg-green-50 border border-green-200 rounded-lg p-4">
				<div className="flex items-start gap-3">
					<CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
					<div>
						<h3 className="font-medium text-green-800 mb-1">
							Account kann gelöscht werden
						</h3>
						<p className="text-sm text-green-700">
							{message}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Main validation message */}
			<div className="bg-red-50 border border-red-200 rounded-lg p-4">
				<div className="flex items-start gap-3">
					<AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
					<div>
						<h3 className="font-medium text-red-800 mb-1">
							Account kann nicht gelöscht werden
						</h3>
						<p className="text-sm text-red-700">
							{message.split('.')[0]}.
						</p>
					</div>
				</div>
			</div>

			{/* Blockers list */}
			<div className="space-y-3">
				<h4 className="font-medium text-gray-800 mb-3">
					Zu erledigende Aufgaben:
				</h4>
				
				{blockers.map((blocker, index) => (
					<div key={index} className={`border rounded-lg p-4 ${getBlockerColor(blocker.type)}`}>
						<div className="flex items-start gap-3">
							{getBlockerIcon(blocker.type)}
							<div className="flex-1">
								<h5 className={`font-medium mb-1 ${getBlockerTextColor(blocker.type)}`}>
									{blocker.message}
								</h5>
								<p className={`text-sm mb-3 ${getBlockerTextColor(blocker.type)}`}>
									{blocker.actionRequired}
								</p>

								{/* Details */}
								{blocker.details.length > 0 && (
									<div className="mt-3 space-y-1">
										{blocker.type === 'group_memberships' 
											? formatGroupDetails(blocker.details)
											: formatEventDetails(blocker.details)
										}
										{blocker.details.length > 3 && (
											<p className="text-sm text-gray-600 font-medium">
												... und {blocker.details.length - 3} weitere
											</p>
										)}
									</div>
								)}

								{/* Action hint */}
								<div className="mt-3 pt-2 border-t border-gray-200">
									<div className="flex items-center gap-2 text-sm text-gray-600">
										<ArrowRight className="h-4 w-4" />
										<span>
											{blocker.type === 'booked_events' && "Gehen Sie zu 'Meine Termine' um Events zu stornieren"}
											{blocker.type === 'applied_events' && "Gehen Sie zu 'Meine Bewerbungen' um Bewerbungen zurückzuziehen"}
											{blocker.type === 'group_memberships' && "Gehen Sie zu 'Gruppenmanagement' um Gruppen zu verlassen"}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>

		</div>
	);
}